<script setup>
import { onMounted, ref } from 'vue';
import { get_invoice_setting, update_invoice_setting } from '@/services/invoice_settings';

const invoiceSetting = ref({
  prefix: 'INV',
  suffix: '',
  next_number: 1,
  number_padding: 5
});

const isLoading = ref(true);
const isSaving = ref(false);
const previewNumber = ref('');

onMounted(async () => {
  await fetchInvoiceSetting();
  isLoading.value = false;
  updatePreview();
});

async function fetchInvoiceSetting() {
  const response = await get_invoice_setting();
  if (!response.error && response.result.success) {
    invoiceSetting.value = response.result.invoice_setting;
  }
}

async function saveSettings() {
  isSaving.value = true;
  const response = await update_invoice_setting(invoiceSetting.value);
  
  if (!response.error && response.result.success) {
    alert('Invoice settings saved successfully!');
  } else {
    alert('Failed to save settings: ' + (response.result?.errors?.join(', ') || 'Unknown error'));
  }
  
  isSaving.value = false;
}

function updatePreview() {
  const num = invoiceSetting.value.next_number.toString().padStart(invoiceSetting.value.number_padding, '0');
  previewNumber.value = `${invoiceSetting.value.prefix}${num}${invoiceSetting.value.suffix}`;
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs main="Invoice Settings" title="Settings" />
    
    <div class="row">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header">
            <h5>Invoice Number Format</h5>
          </div>
          <div class="card-body">
            <loadingCard v-if="isLoading" />
            <form v-else @submit.prevent="saveSettings">
              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="prefix" class="form-label">Prefix</label>
                  <input
                    type="text"
                    class="form-control"
                    id="prefix"
                    v-model="invoiceSetting.prefix"
                    @input="updatePreview"
                    placeholder="e.g., INV"
                  />
                  <small class="text-muted">Text before the invoice number</small>
                </div>
                <div class="col-md-6">
                  <label for="suffix" class="form-label">Suffix</label>
                  <input
                    type="text"
                    class="form-control"
                    id="suffix"
                    v-model="invoiceSetting.suffix"
                    @input="updatePreview"
                    placeholder="e.g., -2024"
                  />
                  <small class="text-muted">Text after the invoice number</small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="next_number" class="form-label">Next Invoice Number</label>
                  <input
                    type="number"
                    class="form-control"
                    id="next_number"
                    v-model.number="invoiceSetting.next_number"
                    @input="updatePreview"
                    min="1"
                  />
                  <small class="text-muted">The next invoice will use this number</small>
                </div>
                <div class="col-md-6">
                  <label for="number_padding" class="form-label">Number Padding (Digits)</label>
                  <input
                    type="number"
                    class="form-control"
                    id="number_padding"
                    v-model.number="invoiceSetting.number_padding"
                    @input="updatePreview"
                    min="1"
                    max="10"
                  />
                  <small class="text-muted">How many digits (e.g., 5 = 00001)</small>
                </div>
              </div>

              <div class="alert alert-info">
                <strong>Preview:</strong> Next invoice number will be: <code>{{ previewNumber }}</code>
              </div>

              <div class="d-flex justify-content-end">
                <button type="submit" class="btn btn-primary" :disabled="isSaving">
                  <span v-if="isSaving">Saving...</span>
                  <span v-else>Save Settings</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card">
          <div class="card-header">
            <h5>Examples</h5>
          </div>
          <div class="card-body">
            <h6>Common Formats:</h6>
            <ul class="list-unstyled">
              <li class="mb-2">
                <code>INV00001</code><br>
                <small>Prefix: INV, Padding: 5</small>
              </li>
              <li class="mb-2">
                <code>INV-2024-00001</code><br>
                <small>Prefix: INV-2024-, Padding: 5</small>
              </li>
              <li class="mb-2">
                <code>2024-INV-0001-MY</code><br>
                <small>Prefix: 2024-INV-, Suffix: -MY, Padding: 4</small>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
